import axios, { AxiosInstance } from "axios";

const getBaseURL = async (): Promise<string> => {
  try {
    const response = await fetch("/config.json");
    const config = await response.json();

    // Determine environment
    const environment = process.env.NODE_ENV === "production" ? "production" : "local";
    return config[environment]?.baseURL || "/";
  } catch (error) {
    console.error("Error loading base URL from config.json:", error);
    return "/";
  }
};

let axiosInstancePrivate: AxiosInstance | null = null;

// Function to initialize the Axios instance dynamically
const initializeAxiosInstance = async (): Promise<AxiosInstance> => {
  if (!axiosInstancePrivate) {
    const baseURL = await getBaseURL(); // Dynamically fetch the base URL
    axiosInstancePrivate = axios.create({
      baseURL: `${baseURL}api/`,
      headers: {
        "Content-Type": "application/json", // Default header for all requests
      },
    });

    // Add request interceptor
    axiosInstancePrivate.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem("token");
        if (token) {
          console.log("User is authorized");
          config.headers.Authorization = `Bearer ${token}`;
        }
      config.headers["Content-Type"] = "application/json";
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Add response interceptor
    axiosInstancePrivate.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Handle 401 Unauthorized
          localStorage.removeItem("token");
          window.location.href = "/login"; // Redirect to login
        }
        return Promise.reject(error);
      }
    );
  }

  return axiosInstancePrivate;
};

// Proxy to automatically resolve and return the Axios instance
export const axiosInstance = new Proxy(
  {},
  {
    get(_, propKey) {
      return async (...args: any[]) => {
        const instance = await initializeAxiosInstance();
        const method = (instance as any)[propKey];
        if (typeof method === "function") {
          return method(...args); // Call Axios methods like get, post, etc.
        }
        return method; // Return properties like interceptors, defaults, etc.
      };
    },
  }
) as AxiosInstance;
